import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2, AlertTriangle, XCircle, HelpCircle, Loader2, RefreshCw, Save,
  Gauge, Wallet, Landmark, Car, ChevronRight, Clock, User,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loanProfilingApi } from '@/services/api';
import { UnderwritingReadiness } from './UnderwritingReadiness';
import { BankMatchPanel } from './BankMatchPanel';

const DECISION = {
  RECOMMEND: { label: 'Recommend', tone: 'emerald', line: 'This file clears the desk.' },
  CONDITIONAL: { label: 'Recommend with conditions', tone: 'amber', line: 'Fundable once the conditions below are cleared.' },
  DECLINE: { label: 'Decline as it stands', tone: 'rose', line: 'A policy rule fails — fix it or route elsewhere.' },
  INCOMPLETE: { label: 'Not enough to decide', tone: 'slate', line: 'Collect the missing inputs for a real call.' },
};
const TONE = {
  emerald: { bg: 'bg-emerald-600', soft: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
  amber: { bg: 'bg-amber-500', soft: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle },
  rose: { bg: 'bg-rose-600', soft: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: XCircle },
  slate: { bg: 'bg-slate-500', soft: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: HelpCircle },
};
const STATUS = {
  pass: { tone: 'emerald', label: 'Clear', icon: CheckCircle2 },
  watch: { tone: 'amber', label: 'Condition', icon: AlertTriangle },
  fail: { tone: 'rose', label: 'Concern', icon: XCircle },
  unknown: { tone: 'slate', label: 'Missing', icon: HelpCircle },
};
const SECTION_ICON = { bureau: Gauge, capacity: Wallet, banking: Landmark, collateral: Car };

const inr = (n) => (n || n === 0 ? `₹${Math.round(n).toLocaleString('en-IN')}` : '—');

const StatusChip = ({ status, hard, testId }) => {
  const s = STATUS[status] || STATUS.unknown;
  const t = TONE[s.tone];
  const Icon = s.icon;
  const label = status === 'fail' && hard ? 'Policy break' : s.label;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${t.soft} ${t.text} border ${t.border}`}
      data-testid={testId}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
};

const CheckRow = ({ check }) => {
  const t = TONE[(STATUS[check.status] || STATUS.unknown).tone];
  return (
    <div className={`grid grid-cols-12 gap-3 px-4 py-3 border-t border-slate-100 ${check.status === 'pass' ? '' : t.soft}`}
      data-testid={`check-${check.key}`}>
      <div className="col-span-12 sm:col-span-4">
        <div className="text-sm font-medium text-slate-800">{check.label}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">Bank norm · {check.norm}</div>
      </div>
      <div className="col-span-7 sm:col-span-3">
        <div className={`text-sm font-semibold ${check.status === 'pass' ? 'text-slate-900' : t.text}`}
          data-testid={`check-value-${check.key}`}>{check.value}</div>
      </div>
      <div className="col-span-5 sm:col-span-2 sm:text-right">
        <StatusChip status={check.status} hard={check.hard} testId={`check-status-${check.key}`} />
      </div>
      <div className="col-span-12 sm:col-span-3">
        <p className="text-xs text-slate-600 leading-relaxed">{check.detail}</p>
        {check.action && (
          <p className="text-xs text-slate-500 mt-1 flex items-start gap-1">
            <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" />{check.action}
          </p>
        )}
      </div>
    </div>
  );
};

const Section = ({ section, index }) => {
  const Icon = SECTION_ICON[section.key] || Gauge;
  const t = TONE[(STATUS[section.status] || STATUS.unknown).tone];
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-4" data-testid={`analysis-section-${section.key}`}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50/70">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-7 h-7 rounded-lg ${t.soft} ${t.text} flex items-center justify-center shrink-0`}>
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">{section.label}</div>
            <div className="text-[11px] text-slate-400 truncate">{section.hint}</div>
          </div>
        </div>
        <StatusChip status={section.status} hard={section.hard_fail} testId={`section-status-${section.key}`} />
      </div>
      {section.checks.map((c) => <CheckRow key={c.key} check={c} />)}
    </motion.div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="px-4 py-3 border-r border-slate-100 last:border-r-0" data-testid={`analysis-stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className="text-base font-semibold text-slate-900 mt-0.5">{value}</div>
    {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
  </div>
);

export const ApplicationAnalysis = ({ leadId, reloadKey, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState('');
  const [tenure, setTenure] = useState('');

  const load = useCallback(async (override, manual = false) => {
    try {
      manual ? setBusy(true) : setLoading(true);
      const res = await loanProfilingApi.underwriting(leadId, override);
      setData(res.data);
      // Always mirror what the engine actually used — it trims the tenure to the
      // product cap, so the inputs must not keep showing an impossible number.
      const a = res.data?.assessment?.assumptions || {};
      if (a.rate != null) setRate(String(a.rate));
      if (a.tenure_months != null) setTenure(String(a.tenure_months));
      if (manual) toast.success('Assessment recalculated');
    } catch (e) {
      if (manual) toast.error('Could not recalculate');
    } finally {
      setLoading(false); setBusy(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const saveSnapshot = async () => {
    setSaving(true);
    try {
      const terms = { rate: Number(rate) || undefined, tenure_months: Number(tenure) || undefined };
      const res = await loanProfilingApi.saveApplicationAnalysis(leadId, terms);
      // Re-read on the same terms so the screen always matches what was saved.
      await load(terms);
      setData((p) => ({ ...p, saved_assessment: res.data.saved_assessment }));
      toast.success('Assessment saved to the file');
      onChanged?.();
    } catch (e) {
      toast.error('Could not save the assessment');
    } finally { setSaving(false); }
  };

  const a = data?.assessment;
  const m = data?.metrics || {};
  const saved = data?.saved_assessment;
  const d = useMemo(() => DECISION[a?.decision] || DECISION.INCOMPLETE, [a?.decision]);
  const t = TONE[d.tone];

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-6 border border-slate-200 rounded-xl" data-testid="analysis-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Running the credit checks…
      </div>
    );
  }
  if (!a) return null;
  const DIcon = t.icon;
  const asm = a.assumptions || {};

  return (
    <div data-testid="application-analysis">
      {/* The call */}
      <div className={`rounded-xl border ${t.border} bg-white overflow-hidden`} data-testid="analysis-decision-card">
        <div className={`${t.bg} px-5 py-4 text-white flex items-start justify-between gap-4 flex-wrap`}>
          <div className="flex items-start gap-3 min-w-0">
            <DIcon className="w-8 h-8 shrink-0 opacity-90" />
            <div className="min-w-0">
              <div className="text-xl font-bold leading-tight" data-testid="analysis-decision">{d.label}</div>
              <p className="text-sm text-white/85 mt-0.5" data-testid="analysis-headline">{a.headline}</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-center">
              <div className="text-2xl font-bold" data-testid="analysis-score">{a.score ?? '—'}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">Score / 100</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" data-testid="analysis-grade">{a.risk_grade || '—'}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">Grade</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" data-testid="analysis-counts">
                {a.counts.pass}/{a.counts.total}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">Checks clear</div>
            </div>
          </div>
        </div>

        {/* Assumptions the call rests on — editable on the call */}
        <div className="flex flex-wrap items-end gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">Loan asked</label>
            <div className="text-lg font-semibold text-slate-900" data-testid="analysis-loan-amount">{inr(asm.loan_amount)}</div>
          </div>
          <div className="w-24">
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">Rate %</label>
            <Input value={rate} onChange={(e) => setRate(e.target.value)} className="h-9" inputMode="decimal"
              data-testid="analysis-rate-input" />
          </div>
          <div className="w-28">
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">Tenure (months)</label>
            <Input value={tenure} onChange={(e) => setTenure(e.target.value)} className="h-9" inputMode="numeric"
              data-testid="analysis-tenure-input" />
          </div>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => load({ rate: Number(rate) || undefined, tenure_months: Number(tenure) || undefined }, true)}
            data-testid="analysis-recalc-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? 'animate-spin' : ''}`} />Recalculate
          </Button>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">EMI at these terms</div>
              <div className="text-lg font-semibold text-slate-900" data-testid="analysis-emi">{inr(asm.emi)}</div>
            </div>
            <Button size="sm" onClick={saveSnapshot} disabled={saving} className="bg-slate-900 hover:bg-slate-800"
              data-testid="analysis-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save assessment
            </Button>
          </div>
        </div>

        {m.tenure_exceeds_age_rule && (
          <div className="px-5 py-2 bg-amber-50 text-xs text-amber-700 border-b border-amber-100" data-testid="analysis-tenure-capped">
            {m.max_tenure_by_age} months is the most this car can take — a {m.tenure_months}-month loan pushes it past
            the {data.policy_summary?.max_vehicle_age_at_maturity}-year age-at-maturity rule.
          </div>
        )}
        {m.tenure_capped_by_product && (
          <div className="px-5 py-2 bg-slate-50 text-xs text-slate-600 border-b border-slate-100" data-testid="analysis-tenure-product-cap">
            Used-car loans run to {m.tenure_months} months at most, so the assessment uses that.
          </div>
        )}

        {/* What the bank will compute */}
        <div className="grid grid-cols-2 md:grid-cols-5 border-b border-slate-100" data-testid="analysis-metrics">
          <Stat label="FOIR" value={m.foir != null ? `${m.foir}%` : '—'} sub={`cap ${data.policy_summary?.foir_max}%`} />
          <Stat label="LTV" value={m.ltv != null ? `${m.ltv}%` : '—'} sub={m.ltv_ceiling ? `ceiling ${m.ltv_ceiling}%` : 'add valuation'} />
          <Stat label="DSCR" value={m.dscr ?? '—'} sub={`min ${data.policy_summary?.dscr_min}`} />
          <Stat label="Car value" value={inr(m.vehicle_value)} sub={m.vehicle_age != null ? `${m.vehicle_age} years old` : 'age unknown'} />
          <Stat label="Monthly income" value={inr(m.net_monthly_income || null)} sub={m.verified_salary_abb ? 'from the statement' : (m.net_monthly_income ? 'as declared' : 'not verified')} />
        </div>

        {saved && (
          <div className="px-5 py-2.5 flex items-center gap-2 text-xs text-slate-500" data-testid="analysis-saved-stamp">
            <Clock className="w-3.5 h-3.5" />
            Last saved as <span className="font-semibold text-slate-700">{(DECISION[saved.decision] || {}).label}</span>
            by <User className="w-3 h-3" /><span className="font-medium text-slate-700">{saved.assessed_by}</span>
            on {new Date(saved.assessed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        )}
      </div>

      {/* Conditions first — the rep's to-do */}
      {(a.conditions.length > 0 || a.missing.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4 mt-5 items-start">
          {a.conditions.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="analysis-conditions">
              <h4 className="text-sm font-semibold text-amber-800 mb-2">To clear this file</h4>
              <ul className="space-y-1.5">
                {a.conditions.map((c) => (
                  <li key={c.key} className="text-xs text-amber-900 flex items-start gap-1.5" data-testid={`analysis-condition-${c.key}`}>
                    <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" /><span><b>{c.label}:</b> {c.action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {a.missing.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="analysis-missing">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Still needed for a firm call</h4>
              <ul className="space-y-1.5">
                {a.missing.map((c) => (
                  <li key={c.key} className="text-xs text-slate-600 flex items-start gap-1.5" data-testid={`analysis-missing-${c.key}`}>
                    <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" /><span><b>{c.label}:</b> {c.action || 'Not captured yet'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* The sheet */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Every check, the way a credit manager reads it</h4>
        {a.sections.map((s, i) => <Section key={s.key} section={s} index={i} />)}
      </div>

      {/* The verdict's own "why" and the documents it drives — same computation, same terms */}
      <UnderwritingReadiness leadId={leadId} data={data} hideVerdictHeader />
      <BankMatchPanel leadId={leadId} reloadKey={`${asm.rate}-${asm.tenure_months}`} />
    </div>
  );
};

export default ApplicationAnalysis;
